import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { Add, Close, PushPinOutlined } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import TeamLogo from "../TeamLogo";
import PlayerHeadshot from "../PlayerHeadshot";
import { SPORTS, SPORT_BY_ID } from "../../constants/sportWorkspace";
import { horizontalScroll } from "../home/homeStyles";
import {
  fanHubAddTileSx,
  fanHubOverlineSx,
  fanHubPaperSx,
  fanHubTileSx,
  sportAccent,
} from "./fanHubStyles";
import { suggestionKeyForPlayer } from "../../utils/fanHubProfile";

const SectionHead = ({ overline, title, hint }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="overline" sx={fanHubOverlineSx}>
      {overline}
    </Typography>
    <Typography variant="h5" fontWeight={800} sx={{ mt: 0.25 }}>
      {title}
    </Typography>
    {hint && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

const EmptyInvite = ({ copy, actionLabel, onAdd }) => (
  <Box
    sx={{
      ...fanHubPaperSx(),
      p: 2.25,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      minWidth: 260,
    }}
  >
    <Typography color="text.secondary" variant="body2">
      {copy}
    </Typography>
    <Box component="button" type="button" onClick={onAdd} sx={fanHubAddTileSx}>
      <Add />
      <Typography variant="caption">{actionLabel}</Typography>
    </Box>
  </Box>
);

export const FanHubTeamRail = ({ teams = [], onAdd, onRemove }) => {
  const empty = teams.length === 0;
  return (
    <Box sx={{ mb: 4 }}>
      <SectionHead
        overline="Who I am"
        title="Favorite teams"
        hint="Up to three per league. First pick is your primary."
      />
      <Box sx={horizontalScroll}>
        {SPORTS.flatMap((sport) =>
          teams
            .filter((team) => team.sport === sport.id)
            .map((team) => (
              <Box
                key={`${team.sport}-${team.teamId}`}
                sx={fanHubTileSx(sport.accent)}
              >
                <IconButton
                  size="small"
                  aria-label={`Remove ${team.name}`}
                  onClick={() => onRemove(team.sport, team.teamId)}
                  sx={{ position: "absolute", top: 2, right: 2 }}
                >
                  <Close sx={{ fontSize: 14 }} />
                </IconButton>
                <TeamLogo
                  sport={team.sport}
                  teamName={team.name}
                  franchiseId={team.teamId}
                  size={48}
                />
                <Typography variant="caption" sx={{ display: "block", mt: 1 }} noWrap>
                  {team.name}
                </Typography>
                <Typography variant="caption" sx={{ color: sport.accent }}>
                  {sport.label}
                </Typography>
              </Box>
            ))
        )}
        {empty ? (
          <EmptyInvite
            copy="Pin the clubs you actually root for — logos, not IDs."
            actionLabel="Add a team"
            onAdd={onAdd}
          />
        ) : (
          <Box component="button" type="button" onClick={onAdd} sx={fanHubAddTileSx}>
            <Add />
            <Typography variant="caption">Add a team</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const FanHubPlayerRail = ({
  players = [],
  suggestions = [],
  onAdd,
  onRemove,
  onPin,
  isPinning,
}) => {
  const empty = players.length === 0;
  return (
    <Box sx={{ mb: 4 }}>
      <SectionHead
        overline="Roster"
        title="Followed players"
        hint="Your watch list. Pin a chart to the dashboard without leaving this page."
      />
      <Box sx={horizontalScroll}>
        {players.map((player) => {
          const accent = sportAccent(player.sport);
          const suggestion = suggestions.find(
            (row) =>
              row.key === suggestionKeyForPlayer(player.sport, player.playerId)
          );
          return (
            <Box
              key={`${player.sport}-${player.playerId}`}
              component={RouterLink}
              to={player.href}
              sx={fanHubTileSx(accent)}
            >
              <IconButton
                size="small"
                aria-label={`Unfollow ${player.name}`}
                onClick={(event) => {
                  event.preventDefault();
                  onRemove(player.sport, player.playerId);
                }}
                sx={{ position: "absolute", top: 2, right: 2 }}
              >
                <Close sx={{ fontSize: 14 }} />
              </IconButton>
              <PlayerHeadshot
                playerId={player.playerId}
                sport={player.sport}
                size={56}
                showTooltip={false}
              />
              <Typography variant="caption" sx={{ display: "block", mt: 1 }} noWrap>
                {player.name}
              </Typography>
              <Typography variant="caption" sx={{ color: accent }}>
                {SPORT_BY_ID[player.sport]?.label}
              </Typography>
              {suggestion && !suggestion.alreadyPinned && (
                <IconButton
                  size="small"
                  aria-label={`Pin ${player.name} to dashboard`}
                  disabled={isPinning}
                  onClick={(event) => {
                    event.preventDefault();
                    onPin(suggestion);
                  }}
                  sx={{ mt: 0.5 }}
                >
                  <PushPinOutlined sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          );
        })}
        {empty ? (
          <EmptyInvite
            copy="Search a name. No player IDs, no spreadsheet energy."
            actionLabel="Follow a player"
            onAdd={onAdd}
          />
        ) : (
          <Box component="button" type="button" onClick={onAdd} sx={fanHubAddTileSx}>
            <Add />
            <Typography variant="caption">Follow a player</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
